import React, { useEffect, useRef } from 'react';
import { AdPlacement } from '@tajawal/web-core-sdk-v2/lib/types/ad-solution.types';
import { BannerSponsored, Button, Icons, Tag, Typography, TYPOGRAPHY_VARIANT } from '@tajawal/ct-web-design-system';
import { buildTestId } from '@/utils/helpers.utils';
import Styled from './AdBanner.styled';
import useAdBanner from '@/hooks/useAdBanner';
import { useTranslation } from 'react-i18next';
import { useAB } from '@tajawal/web-common/lib';
import { AB_TESTING_FLAGS } from '@/constants/analytics.constants';
import { GenericCallback } from '@/types/common.types';
import Image from '../Image';

type AdBannerProps = {
  adBanner: AdPlacement;
  onClick: GenericCallback;
  testIdPrefix: string;
  isDesktop?: boolean;
};

function AdBanner({ adBanner, onClick, testIdPrefix, isDesktop }: AdBannerProps): JSX.Element {
  const testId = buildTestId(testIdPrefix, 'AdBanner', adBanner.position);
  const { t } = useTranslation();
  const observer = useRef(null);
  const bannerRef = useRef(null);
  const isViewedOnce = useRef(false);
  const { active: isSponsoredFlagEnabled } = useAB(AB_TESTING_FLAGS.AD_BANNER_FLAG);
  const { handleAdLoaded, handleAdViewed } = useAdBanner(false);

  useEffect(() => {
    if (adBanner && adBanner.eligibleURL) {
      handleAdLoaded(adBanner);
    }
  }, [adBanner]);

  useEffect(() => {
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !isViewedOnce.current) {
        isViewedOnce.current = true;
        observer.current?.disconnect();
        handleAdViewed(adBanner);
      }
    });

    observer.current.observe(bannerRef.current);

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  if (!adBanner) {
    return null;
  }

  return (
    <Styled.Wrapper ref={bannerRef} $isDesktop={isDesktop}>
      <BannerSponsored
        hideArrow={isDesktop || !adBanner.filters}
        testId={testId}
        layout={isDesktop ? 'desktop' : 'mobile'}
        size="small"
        title={adBanner.title}
        logo={
          <Image layout="fill" height="100%" width="100%" objectFit="cover" src={adBanner.logo_url} alt="Ad Logo" />
        }
        subTitle={adBanner.sub_title}
        tag={
          isSponsoredFlagEnabled ? (
            <Tag appearance="grey" type="tint">
              <Typography variant={TYPOGRAPHY_VARIANT.CAPTIONSEMBLD} component="p">
                {t('AdBanner.Sponsored')}
              </Typography>
            </Tag>
          ) : null
        }
        bannerImg={
          isDesktop && !!adBanner.desktop_image ? (
            <Styled.ImageWrapper data-testid={buildTestId(testId, 'desktopImage')}>
              <Image layout="fill" height="100%" width="100%" alt="banner header" src={adBanner.desktop_image} />
            </Styled.ImageWrapper>
          ) : null
        }
        onClick={onClick}
        cta={
          adBanner.filters ? (
            <Button appearance="primary" size="small" endIcon={<Icons.ChevronForward width="16" height="16" />}>
              {t('AdBanner.View_Flights') as string}
            </Button>
          ) : null
        }
      />
    </Styled.Wrapper>
  );
}

export default AdBanner;
